/**
 * Layer Factory Functions
 *
 * Theme-aware factory functions for creating fill, line, and extrusion layers.
 */

import type { LayerSpecification } from "maplibre-gl";
import type { ThemeConfig, LayerStyle } from "../themes/types";
import { wondermapsDefault } from "../themes/base/wondermaps-default";

/**
 * Get layer style from theme, with fallback chain.
 *
 * 1. Live theme entry (preferred).
 * 2. `wondermapsDefault.layers[layerType]` — covers data-drift cases where the
 *    loaded base theme record predates a LayerType (e.g. `path_wide` /
 *    `path_tertiary` added after the record was saved). This avoids silently
 *    rendering newer path types as custom_polygon, which previously caused
 *    plazas + wide paths + tertiary paths to all pick up the purple
 *    custom_polygon styling on older themes.
 * 3. The theme's own `custom_polygon` (preserved as a last-resort).
 * 4. A built-in custom_polygon-shaped default.
 */
export function getLayerStyleFromTheme(
  layerType: string,
  theme: ThemeConfig
): LayerStyle {
  const key = layerType as keyof typeof theme.layers;
  const style = theme.layers[key];
  if (style) return style;
  const defaultStyle = wondermapsDefault.layers[key];
  if (defaultStyle) return defaultStyle;
  return (
    theme.layers.custom_polygon || {
      fill: "#f3e8ff",
      fillOpacity: 0.5,
      stroke: "#a855f7",
      strokeWidth: 1,
      zOrder: 4,
    }
  );
}

/**
 * Resolve which theme key to read for a layer given its geometry shape.
 *
 * Dual-shape layers (`path_wide`) split visually in the theme:
 * - LineString geometry → `theme.layers.path_wide` (line tokens).
 * - Polygon / MultiPolygon → `theme.layers.plaza` (fill tokens).
 *
 * Same DB layer_type, independent visual editing in the theme editor. All
 * other layer types pass through unchanged.
 */
export function getLayerStyleForGeometry(
  layerType: string,
  geometryType: string,
  theme: ThemeConfig
): LayerStyle {
  if (
    layerType === "path_wide" &&
    (geometryType === "Polygon" || geometryType === "MultiPolygon")
  ) {
    return getLayerStyleFromTheme("plaza", theme);
  }
  return getLayerStyleFromTheme(layerType, theme);
}

/**
 * Create fill layer spec from theme.
 * Used for polygon layer types: boundary, water, vegetation, building, etc.
 * Supports texture patterns via fill-pattern when style.texture is set.
 * Supports depthEffect for pseudo-3D shadow (Disney map technique).
 *
 * @param id - Layer ID
 * @param sourceId - GeoJSON source ID
 * @param style - LayerStyle with fill, stroke, depthEffect
 * @param shadowDirection - Optional global shadow direction (default: SE = {x:1, y:1})
 */
export function createThemedFillLayer(
  id: string,
  sourceId: string,
  style: LayerStyle
): LayerSpecification[] {
  const layers: LayerSpecification[] = [];

  // Main fill layer - use texture pattern if set, otherwise solid color
  if (style.texture) {
    // Textured fill using sprite pattern
    layers.push({
      id: `${id}-fill`,
      type: "fill",
      source: sourceId,
      paint: {
        "fill-pattern": style.texture,
        "fill-opacity": style.fillOpacity ?? 0.5,
      },
    });
  } else if (style.fill) {
    // Solid color fill
    layers.push({
      id: `${id}-fill`,
      type: "fill",
      source: sourceId,
      paint: {
        "fill-color": style.fill,
        "fill-opacity": style.fillOpacity ?? 0.5,
      },
    });
  }

  // Stroke layer (only if stroke defined)
  if (style.stroke) {
    const linePaint: Record<string, unknown> = {
      "line-color": style.stroke,
      "line-width": style.strokeWidth ?? 1,
    };

    layers.push({
      id: `${id}-stroke`,
      type: "line",
      source: sourceId,
      paint: linePaint,
    } as LayerSpecification);
  }

  return layers;
}

/** Clamp maxWidth for dashed lines so dash patterns stay visually stable. */
const DASH_MAX_WIDTH_FACTOR = 3;

export function clampWidthForDash(
  swz: { minZoom: number; maxZoom: number; minWidth: number; maxWidth: number },
  hasDash: boolean
) {
  if (!hasDash) return swz;
  return {
    ...swz,
    maxWidth: Math.min(swz.maxWidth, swz.minWidth * DASH_MAX_WIDTH_FACTOR),
  };
}

/**
 * Create line layer spec(s) from theme.
 * Used for line layer types: path_primary, path_secondary, barrier, rail, etc.
 * Supports strokeTexture, strokeDasharray, lineCap, and casing.
 * Returns array of LayerSpecification (casing layer + main layer when casing enabled).
 */
export function createThemedLineLayer(
  id: string,
  sourceId: string,
  style: LayerStyle
): LayerSpecification[] {
  const layers: LayerSpecification[] = [];
  const hasDash = !!style.strokeDasharray?.length;
  const clamped = style.strokeWidthZoom ? clampWidthForDash(style.strokeWidthZoom, hasDash) : undefined;

  // Build line-width expression
  let lineWidth: unknown;
  if (clamped) {
    const { minZoom, maxZoom, minWidth, maxWidth } = clamped;
    lineWidth = [
      "interpolate",
      ["exponential", 1.5],
      ["zoom"],
      minZoom,
      minWidth,
      maxZoom,
      maxWidth,
    ];
  } else {
    lineWidth = style.strokeWidth ?? 2;
  }

  // Determine line cap: explicit > auto (butt for dashed, round otherwise)
  const lineCap = style.lineCap ?? (style.strokeDasharray?.length ? "butt" : "round");

  // Casing layer (wider line underneath)
  if (style.casing?.enabled) {
    const casingExtra = style.casing.width ?? 2;
    let casingWidth: unknown;
    if (clamped) {
      const { minZoom, maxZoom, minWidth, maxWidth } = clamped;
      casingWidth = [
        "interpolate",
        ["exponential", 1.5],
        ["zoom"],
        minZoom,
        minWidth + casingExtra * 2,
        maxZoom,
        maxWidth + casingExtra * 2,
      ];
    } else {
      casingWidth = (style.strokeWidth ?? 2) + casingExtra * 2;
    }

    layers.push({
      id: `${id}-casing`,
      type: "line",
      source: sourceId,
      layout: {
        "line-cap": lineCap,
        "line-join": "round",
      },
      paint: {
        "line-color": style.casing.color ?? "#000000",
        "line-width": casingWidth,
        "line-opacity": style.lineOpacity ?? 1,
      },
    } as LayerSpecification);
  }

  // Main line layer
  const paint: Record<string, unknown> = {};
  paint["line-width"] = lineWidth;

  if (style.strokeTexture) {
    paint["line-pattern"] = style.strokeTexture;
  } else {
    paint["line-color"] = style.stroke || "#888888";
  }

  paint["line-opacity"] = style.lineOpacity ?? 1;

  if (style.strokeDasharray?.length) {
    paint["line-dasharray"] = style.strokeDasharray;
  }

  layers.push({
    id,
    type: "line",
    source: sourceId,
    layout: {
      "line-cap": lineCap,
      "line-join": "round",
    },
    paint,
  } as LayerSpecification);

  return layers;
}

/**
 * Apply per-layer styleJson overrides to a cloned theme style.
 * Used by both admin and visitor renderers for custom_path layers.
 */
export function applyCustomPathOverrides(
  base: LayerStyle,
  styleJson: unknown
): LayerStyle {
  if (!styleJson || typeof styleJson !== "object") return base;
  const sj = styleJson as Record<string, unknown>;
  if (typeof sj.stroke === "string") return { ...base, stroke: sj.stroke };
  return base;
}

/**
 * Apply per-layer styleJson overrides for custom_polygon layers.
 * Admins can override fill and stroke independently; theme defaults remain
 * the source of truth when no override is set.
 */
export function applyCustomPolygonOverrides(
  base: LayerStyle,
  styleJson: unknown
): LayerStyle {
  if (!styleJson || typeof styleJson !== "object") return base;
  const sj = styleJson as Record<string, unknown>;
  const next: LayerStyle = { ...base };
  if (typeof sj.fill === "string") next.fill = sj.fill;
  if (typeof sj.stroke === "string") next.stroke = sj.stroke;
  return next;
}

/**
 * Create fill-extrusion layer spec for 3D buildings.
 * Extrudes polygons vertically to create 3D effect.
 *
 * @param id - Layer ID
 * @param sourceId - GeoJSON source ID
 * @param style - LayerStyle with fill color
 * @param height - Extrusion height in meters (default 10)
 * @returns MapLibre fill-extrusion layer spec
 */
export function createThemedFillExtrusion(
  id: string,
  sourceId: string,
  style: LayerStyle,
  height: number = 10
): LayerSpecification {
  return {
    id,
    type: "fill-extrusion",
    source: sourceId,
    paint: {
      "fill-extrusion-color": style.fill || "#6b7280",
      "fill-extrusion-height": height,
      "fill-extrusion-base": 0,
      "fill-extrusion-opacity": style.fillOpacity ?? 0.9,
      "fill-extrusion-vertical-gradient": true,
    },
  } as LayerSpecification;
}
